import { Badge } from 'antd';
import { useLazyGetAllAbsenceQuery } from 'app/services/edpApiV1/absence';
import { TableArchive } from 'features/tables';
import { useEffect, useState } from 'react';
import { SortCondition, ZupRequestStatusType } from 'shared/enums';
import { dataForAbsencesTable, sortingDate } from 'shared/helpers/utils';
import { usePullSort } from 'shared/hooks';
import { RequestsSearch, TColumns } from 'shared/types/types';
import { useIndicators } from 'features/indicators';
import { TPreparedAbsenceData } from 'shared/types/absence.types';
import { SearchKeysNews } from 'shared/enums/news';
import { useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import style from './index.module.scss';

const AbsenceArchive = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataAbsence, setDataAbsence] = useState<TPreparedAbsenceData[] | []>([]);
  const [sortInfo, trigger] = usePullSort();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: _ => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'Период',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status, render) => (
        <div className={style.status}>
          <span>
            <Badge status={status.type} />
            {status.name}
          </span>
          <span>
            <Badge status={(render as TPreparedAbsenceData).zupRequestStatus?.type} />
            {(render as TPreparedAbsenceData).zupRequestStatus?.type === ZupRequestStatusType.success
              ? 'Выгружено в 1С'
              : null}
            {(render as TPreparedAbsenceData).zupRequestStatus?.type === ZupRequestStatusType.processing
              ? 'Отправлено в 1С'
              : null}
            {(render as TPreparedAbsenceData).zupRequestStatus?.type === ZupRequestStatusType.error
              ? 'Ошибка выгрузки'
              : null}
          </span>
        </div>
      ),
    },
  ];

  const [getAllAbsenceTrigger, resultAllAbsence] = useLazyGetAllAbsenceQuery();

  useEffect(() => {
    if (sortInfo.value === null)
      getAllAbsenceTrigger({
        type: 'myArchive',
        page: currentPage,
        size: currentSize,
        allAvailableOrganizations,
        ...search,
      });
    if (sortInfo.value === SortCondition.ASCEND)
      getAllAbsenceTrigger({
        type: 'myArchive',
        page: currentPage,
        size: currentSize,
        order: SortCondition.ASC,
        allAvailableOrganizations,
        ...search,
      });
    if (sortInfo.value === SortCondition.DESCEND)
      getAllAbsenceTrigger({
        type: 'myArchive',
        page: currentPage,
        size: currentSize,
        order: SortCondition.DESC,
        allAvailableOrganizations,
        ...search,
      });
  }, [getAllAbsenceTrigger, currentPage, sortInfo.value, currentSize, search, allAvailableOrganizations]);

  useEffect(() => {
    if (resultAllAbsence && resultAllAbsence?.data?.data) {
      const dataTable = dataForAbsencesTable(resultAllAbsence?.data?.data);
      if (dataTable) {
        setDataAbsence(dataTable);
      }
    }
  }, [resultAllAbsence]);

  useIndicators(resultAllAbsence.isSuccess, resultAllAbsence.isFetching);

  return (
    <>
      {dataAbsence && (
        <TableArchive
          dataSource={dataAbsence}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          currentSize={currentSize}
          setCurrentSize={setCurrentSize}
          total={resultAllAbsence?.data?.pagination?.total}
          tabKey="myArchive"
          // для раздела "Неявки" запись открывается по клику строки
          rowSelectionType={false}
          onSearch={onSearch}
        />
      )}
    </>
  );
};

export default AbsenceArchive;
